import { AdminPage, AdminPageHeader } from '../../components/layout/AdminPage';
/**
 * @page DevToolsPage
 * @description Developer tools: Permission Debug, Log Analytics, WebSocket Tester
 * Requires 最高管理員 role.
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { roleDisplayName } from '../../lib/roleDisplay';
import { io, Socket } from 'socket.io-client';
import { api } from '../../lib/api';
import { logViewerIdentity } from './AdminLogsPage';
import { useAuthStore } from '../../store/auth.store';
import { PATH_FEATURE_MAP } from '../../config/path-feature-map';
import {
  buildMetadataValue,
  classifyRevisionStatus,
  classifyVersionStatus,
  UPDATE_LOG,
  webBuildMetadata,
  type BuildMetadata,
} from '../../lib/versionUpdates';

const WS_URL = (import.meta as unknown as { env: { VITE_WS_URL?: string } }).env.VITE_WS_URL ?? '';

type Tab = 'permissions' | 'logs' | 'websocket' | 'versions';

// ============================================================
// Types
// ============================================================

interface DebugRole {
  roleId: string;
  roleName: string;
  isSystemRole: boolean;
  pagePermissions: { pagePath: string; isVisible: boolean }[];
  actionPermissions: { actionKey: string; isAllowed: boolean }[];
}

interface DebugResult {
  user: { id: string; username: string; isActive: boolean; isSystemUser: boolean };
  roles: DebugRole[];
  merged: { visiblePages: string[]; allowedActions: string[] };
  editionInfo: { edition: string; enabledFeatures: string[] };
}

interface UserListItem {
  id: string;
  username: string;
  isActive: boolean;
}

interface LogStats {
  summary: { total24h: number; total7d: number; totalAll: number };
  byLevel: { level: string; count: number }[];
  byCategory: { category: string; count: number }[];
  hourly: { hour: string; count: number }[];
  daily: { day: string; count: number }[];
  topActors: { username: string; count: number }[];
}

interface WsEvent {
  id: number;
  time: string;
  direction: 'in' | 'out';
  event: string;
  data: unknown;
}

// ============================================================
// Main Component
// ============================================================

function DevToolsPage() {
  const { t } = useTranslation('admin');
  const [tab, setTab] = useState<Tab>('permissions');

  const tabs: { key: Tab; label: string }[] = [
    { key: 'permissions', label: t('devTools.permissions', { defaultValue: 'Permission Debug' }) },
    { key: 'logs', label: t('devTools.logAnalytics', { defaultValue: 'Log Analytics' }) },
    { key: 'websocket', label: t('devTools.wsTester', { defaultValue: 'WebSocket Tester' }) },
    { key: 'versions', label: 'Version & Updates' },
  ];

  return (
    <AdminPage
      className="admin-page-body"
      header={
        <AdminPageHeader
          title={t('devTools.title', { defaultValue: 'Developer Tools' })}
          description={t('devTools.subtitle', {
            defaultValue: 'Permission debugging, log analytics, and WebSocket testing',
          })}
        />
      }
    >
      {/* Tab Bar */}
      <div className="overflow-x-auto mb-6">
        <div className="flex flex-wrap gap-2 bg-base-200 rounded-lg p-1">
          {tabs.map((tb) => (
            <button
              key={tb.key}
              onClick={() => setTab(tb.key)}
              className={`min-h-11 px-4 py-2 rounded-md text-sm font-medium whitespace-nowrap transition-colors ${
                tab === tb.key
                  ? 'bg-primary text-primary-content shadow-xs'
                  : 'text-base-content/60 hover:text-base-content hover:bg-base-300'
              }`}
            >
              {tb.label}
            </button>
          ))}
        </div>
      </div>

      {tab === 'permissions' && <PermissionDebugTab />}
      {tab === 'logs' && <LogAnalyticsTab />}
      {tab === 'websocket' && <WebSocketTesterTab />}
      {tab === 'versions' && <VersionUpdatesTab />}
    </AdminPage>
  );
}

function VersionUpdatesTab() {
  const { data: health, isLoading } = useQuery<BuildMetadata & { status: string }>({
    queryKey: ['health-version'],
    queryFn: async () => {
      const response = await fetch('/health');
      if (!response.ok) throw new Error(`Health request failed: ${response.status}`);
      return response.json() as Promise<BuildMetadata & { status: string }>;
    },
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
  const webMetadata = webBuildMetadata();
  const apiMetadata: BuildMetadata = {
    version: health?.version ?? null,
    gitRevision: health?.gitRevision ?? null,
    environment: health?.environment ?? null,
    buildTime: health?.buildTime ?? null,
  };
  const status = classifyVersionStatus(__APP_VERSION__, health?.version ?? undefined);
  const revisionStatus = classifyRevisionStatus(webMetadata.gitRevision, apiMetadata.gitRevision);
  const statusView = {
    synchronized: { label: 'Versions synchronized', className: 'text-success bg-success/10' },
    'update-available': { label: 'Update available', className: 'text-warning bg-warning/10' },
    mismatch: { label: 'Version mismatch', className: 'text-warning bg-warning/10' },
    unavailable: {
      label: isLoading ? 'Checking API version' : 'API version unavailable',
      className: 'text-base-content/50 bg-base-300',
    },
  }[status.kind];
  const revisionStatusView = {
    synchronized: { label: 'Revisions synchronized', className: 'text-success bg-success/10' },
    mismatch: { label: 'Revision mismatch', className: 'text-warning bg-warning/10' },
    unavailable: {
      label: isLoading ? 'Checking API revision' : 'Revision unavailable',
      className: 'text-base-content/50 bg-base-300',
    },
  }[revisionStatus];
  const metadataRows: { label: string; key: keyof BuildMetadata }[] = [
    { label: 'Version', key: 'version' },
    { label: 'Git revision', key: 'gitRevision' },
    { label: 'Environment', key: 'environment' },
    { label: 'Build time', key: 'buildTime' },
  ];

  return (
    <div className="space-y-6">
      <section className="bg-base-200 rounded-xl border border-base-300 p-4">
        <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
          <div>
            <h2 className="text-lg font-semibold text-base-content">Application Version</h2>
            <p className="text-sm text-base-content/50">Compiled Web build and live API health</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <span className={`px-3 py-1 rounded-full text-xs font-medium ${statusView.className}`}>
              {statusView.label}
            </span>
            <span
              className={`px-3 py-1 rounded-full text-xs font-medium ${revisionStatusView.className}`}
            >
              {revisionStatusView.label}
            </span>
          </div>
        </div>
        <dl className="grid gap-3 sm:grid-cols-2">
          {[
            { label: 'Web', metadata: webMetadata },
            { label: 'API', metadata: apiMetadata },
          ].map(({ label, metadata }) => (
            <div key={label} className="rounded-lg bg-base-300 p-4">
              <dt className="text-xs uppercase tracking-wide text-base-content/40">{label}</dt>
              <dd className="mt-3">
                <dl className="space-y-3">
                  {metadataRows.map((row) => (
                    <div key={row.key}>
                      <dt className="text-xs text-base-content/40">{row.label}</dt>
                      <dd className="font-mono text-sm break-all">
                        {buildMetadataValue(metadata[row.key])}
                      </dd>
                    </div>
                  ))}
                </dl>
              </dd>
            </div>
          ))}
        </dl>
      </section>

      <section>
        <h2 className="text-lg font-semibold text-base-content mb-3">Update Log</h2>
        <div className="space-y-3">
          {UPDATE_LOG.map((entry) => (
            <article
              key={entry.version}
              className="bg-base-200 rounded-xl border border-base-300 p-4"
            >
              <div className="flex flex-wrap items-baseline justify-between gap-2 mb-3">
                <h3 className="font-mono text-base font-semibold">v{entry.version}</h3>
                <time className="text-xs text-base-content/40" dateTime={entry.date}>
                  {entry.date}
                </time>
              </div>
              <ul className="list-disc pl-5 space-y-1 text-sm text-base-content/70">
                {entry.changes.map((change) => (
                  <li key={change}>{change}</li>
                ))}
              </ul>
            </article>
          ))}
        </div>
      </section>
    </div>
  );
}

// ============================================================
// Tab 1: Permission Debug
// ============================================================

function PermissionDebugTab() {
  const { t } = useTranslation('admin');
  const [selectedUserId, setSelectedUserId] = useState('');
  const [pathQuery, setPathQuery] = useState('');

  // Fetch user list
  const { data: users } = useQuery<UserListItem[]>({
    queryKey: ['devtools-users'],
    queryFn: () => api.get('/users'),
  });

  // Fetch debug permissions for selected user
  const { data: debug, isLoading } = useQuery<DebugResult>({
    queryKey: ['devtools-perms', selectedUserId],
    queryFn: () => api.get(`/auth/debug-permissions/${selectedUserId}`),
    enabled: !!selectedUserId,
  });

  const allPaths = Object.keys(PATH_FEATURE_MAP).concat(
    debug?.merged.visiblePages.filter((p) => !PATH_FEATURE_MAP[p]) ?? [],
  );
  const uniquePaths = [...new Set(allPaths)].sort();
  const filteredPaths = pathQuery
    ? uniquePaths.filter((p) => p.toLowerCase().includes(pathQuery.toLowerCase()))
    : uniquePaths;

  return (
    <div className="space-y-6">
      {/* User Selector */}
      <div className="bg-base-200 rounded-xl border border-base-300 p-4">
        <label className="text-sm font-medium text-base-content/70 mb-2 block">
          {t('devTools.selectUser', { defaultValue: 'Select User' })}
        </label>
        <select
          value={selectedUserId}
          onChange={(e) => setSelectedUserId(e.target.value)}
          className="w-full max-w-md px-3 py-2 rounded-lg border border-base-300 bg-base-300 text-base-content text-sm"
        >
          <option value="">
            -- {t('devTools.chooseUser', { defaultValue: 'Choose a user' })} --
          </option>
          {(users ?? []).map((u) => (
            <option key={u.id} value={u.id}>
              {u.username} {!u.isActive ? '(inactive)' : ''}
            </option>
          ))}
        </select>
      </div>

      {isLoading && <div className="text-center text-base-content/40 py-8">Loading...</div>}

      {debug && (
        <>
          {/* User Info */}
          <div className="bg-base-200 rounded-xl border border-base-300 p-4">
            <h3 className="text-sm font-semibold text-base-content mb-3">User Info</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
              <div>
                <span className="text-base-content/50">Username:</span>{' '}
                <span className="font-mono">{debug.user.username}</span>
              </div>
              <div>
                <span className="text-base-content/50">Active:</span>{' '}
                <span className={debug.user.isActive ? 'text-success' : 'text-error'}>
                  {debug.user.isActive ? 'Yes' : 'No'}
                </span>
              </div>
              <div>
                <span className="text-base-content/50">System User:</span>{' '}
                <span className={debug.user.isSystemUser ? 'text-warning' : ''}>
                  {debug.user.isSystemUser ? 'Yes' : 'No'}
                </span>
              </div>
              <div>
                <span className="text-base-content/50">Edition:</span>{' '}
                <span className="font-mono">{debug.editionInfo.edition}</span>
              </div>
            </div>
          </div>

          {/* Roles */}
          <div className="bg-base-200 rounded-xl border border-base-300 p-4">
            <h3 className="text-sm font-semibold text-base-content mb-3">
              Assigned Roles ({debug.roles.length})
            </h3>
            <div className="flex flex-wrap gap-2">
              {debug.roles.map((r) => (
                <span
                  key={r.roleId}
                  className={`px-3 py-1 rounded-full text-sm ${
                    r.isSystemRole
                      ? 'bg-warning/20 text-warning border border-warning/30'
                      : 'bg-primary/15 text-primary border border-primary/20'
                  }`}
                >
                  {roleDisplayName({ name: r.roleName, isSystemRole: r.isSystemRole }, t)}
                  {r.isSystemRole && ' (system)'}
                </span>
              ))}
            </div>
          </div>

          {/* Page Access Resolution Chain */}
          <div className="bg-base-200 rounded-xl border border-base-300 p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-base-content">
                Page Access Resolution ({filteredPaths.length} paths)
              </h3>
              <input
                type="text"
                value={pathQuery}
                onChange={(e) => setPathQuery(e.target.value)}
                placeholder="Filter paths..."
                className="px-3 py-1.5 rounded-lg border border-base-300 bg-base-300 text-base-content text-xs w-48"
              />
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-base-300 text-left">
                    <th className="py-2 pr-3 text-base-content/50 font-medium">Path</th>
                    <th className="py-2 px-3 text-base-content/50 font-medium text-center">
                      Edition Gate
                    </th>
                    <th className="py-2 px-3 text-base-content/50 font-medium text-center">
                      RBAC Gate
                    </th>
                    <th className="py-2 px-3 text-base-content/50 font-medium text-center">
                      Final
                    </th>
                    <th className="py-2 pl-3 text-base-content/50 font-medium">Granted By</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-base-300/50">
                  {filteredPaths.map((path) => {
                    const requiredFeature = PATH_FEATURE_MAP[path];
                    const editionPass = debug.user.isSystemUser
                      ? true
                      : !requiredFeature ||
                        debug.editionInfo.enabledFeatures.includes(requiredFeature);
                    const rbacPass = debug.merged.visiblePages.includes(path);
                    const finalPass = editionPass && rbacPass;

                    // Which role(s) grant this page
                    const grantedBy = debug.roles
                      .filter((r) =>
                        r.pagePermissions.some((p) => p.pagePath === path && p.isVisible),
                      )
                      .map((r) =>
                        roleDisplayName({ name: r.roleName, isSystemRole: r.isSystemRole }, t),
                      );

                    return (
                      <tr key={path} className={finalPass ? '' : 'opacity-50'}>
                        <td className="py-1.5 pr-3 font-mono text-xs">{path}</td>
                        <td className="py-1.5 px-3 text-center">
                          {requiredFeature ? (
                            <span
                              className={`text-xs px-1.5 py-0.5 rounded ${editionPass ? 'bg-success/15 text-success' : 'bg-error/15 text-error'}`}
                            >
                              {editionPass ? 'PASS' : 'BLOCKED'}
                            </span>
                          ) : (
                            <span className="text-xs text-base-content/30">N/A</span>
                          )}
                        </td>
                        <td className="py-1.5 px-3 text-center">
                          <span
                            className={`text-xs px-1.5 py-0.5 rounded ${rbacPass ? 'bg-success/15 text-success' : 'bg-error/15 text-error'}`}
                          >
                            {rbacPass ? 'PASS' : 'DENIED'}
                          </span>
                        </td>
                        <td className="py-1.5 px-3 text-center">
                          <span
                            className={`text-xs font-bold px-1.5 py-0.5 rounded ${finalPass ? 'bg-success/20 text-success' : 'bg-error/20 text-error'}`}
                          >
                            {finalPass ? 'VISIBLE' : 'HIDDEN'}
                          </span>
                        </td>
                        <td className="py-1.5 pl-3 text-xs text-base-content/60">
                          {grantedBy.length > 0 ? grantedBy.join(', ') : '-'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Allowed Actions */}
          <div className="bg-base-200 rounded-xl border border-base-300 p-4">
            <h3 className="text-sm font-semibold text-base-content mb-3">
              Allowed Actions ({debug.merged.allowedActions.length})
            </h3>
            <div className="flex flex-wrap gap-1.5">
              {debug.merged.allowedActions.sort().map((action) => {
                const grantedBy = debug.roles
                  .filter((r) =>
                    r.actionPermissions.some((a) => a.actionKey === action && a.isAllowed),
                  )
                  .map((r) =>
                    roleDisplayName({ name: r.roleName, isSystemRole: r.isSystemRole }, t),
                  );
                return (
                  <span
                    key={action}
                    title={`Granted by: ${grantedBy.join(', ')}`}
                    className="px-2 py-0.5 rounded bg-success/10 text-success text-xs font-mono cursor-default"
                  >
                    {action}
                  </span>
                );
              })}
            </div>
          </div>

          {/* Edition Features */}
          <div className="bg-base-200 rounded-xl border border-base-300 p-4">
            <h3 className="text-sm font-semibold text-base-content mb-3">
              Edition: {debug.editionInfo.edition} ({debug.editionInfo.enabledFeatures.length}{' '}
              features)
            </h3>
            <div className="flex flex-wrap gap-1.5">
              {debug.editionInfo.enabledFeatures.sort().map((f) => (
                <span
                  key={f}
                  className="px-2 py-0.5 rounded bg-info/10 text-info text-xs font-mono"
                >
                  {f}
                </span>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ============================================================
// Tab 2: Log Analytics
// ============================================================

const LEVEL_COLORS: Record<string, string> = {
  INFO: 'bg-info/20 text-info',
  WARNING: 'bg-warning/20 text-warning',
  ERROR: 'bg-error/20 text-error',
  CRITICAL: 'bg-error/30 text-error',
  DEBUG: 'bg-base-300 text-base-content/50',
  VERBOSE: 'bg-base-300 text-base-content/30',
};

const CATEGORY_COLORS: Record<string, string> = {
  AUTH: 'bg-purple-500/15 text-purple-400',
  MEMBER: 'bg-blue-500/15 text-blue-400',
  ACCOUNTING: 'bg-green-500/15 text-green-400',
  TOURNAMENT: 'bg-amber-500/15 text-amber-400',
  SYSTEM: 'bg-slate-500/15 text-slate-400',
  SECURITY: 'bg-red-500/15 text-red-400',
  ADMIN: 'bg-cyan-500/15 text-cyan-400',
  DISPLAY: 'bg-pink-500/15 text-pink-400',
};

function LogAnalyticsTab() {
  const { t } = useTranslation('admin');
  const token = useAuthStore((s) => s.accessToken);
  const userId = useAuthStore((s) => s.user?.id);
  const revision = useAuthStore((s) => s.authRevision);
  const authenticated = useAuthStore((s) => s.isAuthenticated);
  const viewer = authenticated ? logViewerIdentity(token, userId) : null;

  const { data: stats, isLoading } = useQuery<LogStats>({
    queryKey: ['devtools-log-stats', viewer?.userId, viewer?.venueId, revision],
    enabled: Boolean(viewer),
    queryFn: () => api.get('/logs/stats'),
    refetchInterval: 30000, // refresh every 30s
  });

  if (!viewer)
    return (
      <p role="alert" className="text-error">
        {t('logAccessDenied')}
      </p>
    );

  if (isLoading || !stats) {
    return <div className="text-center text-base-content/40 py-8">Loading log statistics...</div>;
  }

  const maxHourly = stats.hourly.reduce((m, h) => Math.max(m, h.count), 1);
  const maxDaily = stats.daily.reduce((m, d) => Math.max(m, d.count), 1);
  const maxByLevel = stats.byLevel.reduce((m, i) => Math.max(m, i.count), 1);
  const maxByCategory = stats.byCategory.reduce((m, i) => Math.max(m, i.count), 1);

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-3 gap-4">
        {[
          { label: 'Last 24h', value: stats.summary.total24h },
          { label: 'Last 7 days', value: stats.summary.total7d },
          { label: 'All time', value: stats.summary.totalAll },
        ].map((card) => (
          <div
            key={card.label}
            className="bg-base-200 rounded-xl border border-base-300 p-4 text-center"
          >
            <div className="text-2xl font-bold text-base-content font-mono">
              {card.value.toLocaleString()}
            </div>
            <div className="text-xs text-base-content/50 mt-1">{card.label}</div>
          </div>
        ))}
      </div>

      {/* By Level + By Category side by side */}
      <div className="grid grid-cols-2 gap-4">
        <div className="bg-base-200 rounded-xl border border-base-300 p-4">
          <h3 className="text-sm font-semibold text-base-content mb-3">By Level (24h)</h3>
          <div className="space-y-2">
            {stats.byLevel
              .sort((a, b) => b.count - a.count)
              .map((item) => (
                <div key={item.level} className="flex items-center gap-2">
                  <span
                    className={`text-xs font-medium px-2 py-0.5 rounded w-20 text-center ${LEVEL_COLORS[item.level] ?? ''}`}
                  >
                    {item.level}
                  </span>
                  <div className="flex-1 bg-base-300 rounded-full h-4 overflow-hidden">
                    <div
                      className="h-full bg-primary/40 rounded-full transition-all"
                      style={{ width: `${(item.count / maxByLevel) * 100}%` }}
                    />
                  </div>
                  <span className="text-xs font-mono text-base-content/60 w-12 text-right">
                    {item.count}
                  </span>
                </div>
              ))}
          </div>
        </div>

        <div className="bg-base-200 rounded-xl border border-base-300 p-4">
          <h3 className="text-sm font-semibold text-base-content mb-3">By Category (24h)</h3>
          <div className="space-y-2">
            {stats.byCategory
              .sort((a, b) => b.count - a.count)
              .map((item) => (
                <div key={item.category} className="flex items-center gap-2">
                  <span
                    className={`text-xs font-medium px-2 py-0.5 rounded w-24 text-center ${CATEGORY_COLORS[item.category] ?? 'bg-base-300'}`}
                  >
                    {item.category}
                  </span>
                  <div className="flex-1 bg-base-300 rounded-full h-4 overflow-hidden">
                    <div
                      className="h-full bg-primary/40 rounded-full transition-all"
                      style={{ width: `${(item.count / maxByCategory) * 100}%` }}
                    />
                  </div>
                  <span className="text-xs font-mono text-base-content/60 w-12 text-right">
                    {item.count}
                  </span>
                </div>
              ))}
          </div>
        </div>
      </div>

      {/* Hourly Chart (24h) */}
      <div className="bg-base-200 rounded-xl border border-base-300 p-4">
        <h3 className="text-sm font-semibold text-base-content mb-3">Hourly Volume (Last 24h)</h3>
        <div className="flex items-end gap-[2px] h-32">
          {stats.hourly.map((h) => {
            const pct = (h.count / maxHourly) * 100;
            const hour = new Date(h.hour).getHours().toString().padStart(2, '0');
            return (
              <div
                key={h.hour}
                className="flex-1 group relative"
                title={`${hour}:00 — ${h.count} logs`}
              >
                <div
                  className="bg-primary/50 hover:bg-primary/70 rounded-t transition-colors w-full"
                  style={{ height: `${Math.max(pct, 2)}%` }}
                />
                <div className="text-[9px] text-base-content/30 text-center mt-1 hidden group-hover:block">
                  {hour}
                </div>
              </div>
            );
          })}
        </div>
        <div className="flex justify-between text-[10px] text-base-content/30 mt-1">
          <span>{stats.hourly[0] ? new Date(stats.hourly[0].hour).getHours() + ':00' : ''}</span>
          <span>now</span>
        </div>
      </div>

      {/* Daily Chart (7d) */}
      <div className="bg-base-200 rounded-xl border border-base-300 p-4">
        <h3 className="text-sm font-semibold text-base-content mb-3">Daily Volume (Last 7 days)</h3>
        <div className="flex items-end gap-2 h-24">
          {stats.daily.map((d) => {
            const pct = (d.count / maxDaily) * 100;
            const label = new Date(d.day).toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
            });
            return (
              <div key={d.day} className="flex-1 text-center">
                <div
                  className="bg-info/40 hover:bg-info/60 rounded-t transition-colors mx-auto"
                  style={{ height: `${Math.max(pct, 4)}%`, width: '80%' }}
                />
                <div className="text-[10px] text-base-content/40 mt-1">{label}</div>
                <div className="text-[10px] font-mono text-base-content/50">{d.count}</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Top Actors */}
      <div className="bg-base-200 rounded-xl border border-base-300 p-4">
        <h3 className="text-sm font-semibold text-base-content mb-3">Top Actors (Last 24h)</h3>
        <div className="space-y-1.5">
          {stats.topActors.map((actor, i) => (
            <div key={actor.username} className="flex items-center gap-3">
              <span className="text-xs text-base-content/30 w-4">{i + 1}.</span>
              <span className="text-sm text-primary font-mono">@{actor.username}</span>
              <span className="text-xs font-mono text-base-content/50">{actor.count} events</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ============================================================
// Tab 3: WebSocket Tester
// ============================================================

const WS_NAMESPACES = [
  { value: '/tournament', label: '/tournament', requiresJwt: true },
  { value: '/display', label: '/display', requiresToken: true },
  { value: '/logs', label: '/logs', requiresJwt: true },
] as const;

function WebSocketTesterTab() {
  const { t } = useTranslation('admin');
  const token = useAuthStore((s) => s.accessToken);
  const [namespace, setNamespace] = useState('/tournament');
  const [displayToken, setDisplayToken] = useState('');
  const [connected, setConnected] = useState(false);
  const [events, setEvents] = useState<WsEvent[]>([]);
  const [emitEvent, setEmitEvent] = useState('');
  const [emitPayload, setEmitPayload] = useState('{}');
  const socketRef = useRef<Socket | null>(null);
  const eventIdRef = useRef(0);
  const eventsEndRef = useRef<HTMLDivElement>(null);

  const addEvent = useCallback((direction: 'in' | 'out', event: string, data: unknown) => {
    setEvents((prev) =>
      [
        ...prev,
        {
          id: ++eventIdRef.current,
          time: new Date().toLocaleTimeString('en-US', {
            hour12: false,
            fractionalSecondDigits: 3,
          }),
          direction,
          event,
          data,
        },
      ].slice(-200),
    ); // Keep last 200
  }, []);

  const handleConnect = useCallback(() => {
    if (socketRef.current) {
      socketRef.current.removeAllListeners();
      socketRef.current.disconnect();
      socketRef.current = null;
      setConnected(false);
      addEvent('out', 'disconnect', { manual: true });
      return;
    }

    const nsConfig = WS_NAMESPACES.find((n) => n.value === namespace);
    const authConfig: Record<string, string> = {};

    if (nsConfig && 'requiresJwt' in nsConfig && nsConfig.requiresJwt) {
      if (!token) {
        addEvent('in', 'error', { message: 'No JWT token available' });
        return;
      }
      authConfig.token = token;
    }
    if (nsConfig && 'requiresToken' in nsConfig && nsConfig.requiresToken && displayToken) {
      authConfig.credential = displayToken;
    }

    addEvent('out', 'connecting', { namespace, auth: Object.keys(authConfig) });

    const socket = io(`${WS_URL}${namespace}`, {
      auth: authConfig,
      transports: ['websocket'],
      reconnection: false,
    });

    socketRef.current = socket;

    socket.on('connect', () => {
      setConnected(true);
      addEvent('in', 'connect', { id: socket.id, transport: socket.io.engine?.transport?.name });
    });

    socket.on('disconnect', (reason) => {
      setConnected(false);
      addEvent('in', 'disconnect', { reason });
    });

    socket.on('connect_error', (err) => {
      setConnected(false);
      addEvent('in', 'connect_error', { message: err.message });
    });

    // Catch-all listener for any event
    socket.onAny((event, ...args) => {
      addEvent('in', event, args.length === 1 ? args[0] : args);
    });
  }, [namespace, token, displayToken, addEvent]);

  const handleEmit = useCallback(() => {
    if (!socketRef.current || !emitEvent.trim()) return;
    let payload: unknown;
    try {
      payload = JSON.parse(emitPayload);
    } catch {
      addEvent('in', 'error', { message: 'Invalid JSON payload' });
      return;
    }
    socketRef.current.emit(emitEvent.trim(), payload);
    addEvent('out', emitEvent.trim(), payload);
  }, [emitEvent, emitPayload, addEvent]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      socketRef.current?.disconnect();
      socketRef.current = null;
    };
  }, []);

  // Auto-scroll
  useEffect(() => {
    eventsEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [events]);

  // Preset emit templates
  const presets: { label: string; event: string; payload: string; ns: string }[] = [
    {
      label: 'Join Tournament',
      event: 'tournament:join',
      payload: '{"tournamentId": ""}',
      ns: '/tournament',
    },
    {
      label: 'Timer Action: Pause',
      event: 'tournament:timer:action',
      payload: '{"tournamentId": "", "action": {"type": "pause"}}',
      ns: '/tournament',
    },
    {
      label: 'Ping',
      event: 'ping',
      payload: '{}',
      ns: '/tournament',
    },
  ];

  return (
    <div className="space-y-4">
      {/* Connection Panel */}
      <div className="bg-base-200 rounded-xl border border-base-300 p-4">
        <div className="flex items-end gap-3 flex-wrap">
          <div>
            <label className="text-xs text-base-content/50 block mb-1">Namespace</label>
            <select
              value={namespace}
              onChange={(e) => setNamespace(e.target.value)}
              disabled={connected}
              className="px-3 py-2 rounded-lg border border-base-300 bg-base-300 text-base-content text-sm w-40"
            >
              {WS_NAMESPACES.map((ns) => (
                <option key={ns.value} value={ns.value}>
                  {ns.label}
                </option>
              ))}
            </select>
          </div>

          {namespace === '/display' && (
            <div>
              <label className="text-xs text-base-content/50 block mb-1">Display Token</label>
              <input
                type="text"
                value={displayToken}
                onChange={(e) => setDisplayToken(e.target.value)}
                placeholder="Screen token..."
                disabled={connected}
                className="px-3 py-2 rounded-lg border border-base-300 bg-base-300 text-base-content text-sm w-48"
              />
            </div>
          )}

          <button
            onClick={handleConnect}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              connected
                ? 'bg-error/20 text-error hover:bg-error/30'
                : 'bg-success/20 text-success hover:bg-success/30'
            }`}
          >
            {connected ? 'Disconnect' : 'Connect'}
          </button>

          <div className="flex items-center gap-2 ml-auto">
            <span
              className={`w-2.5 h-2.5 rounded-full ${connected ? 'bg-success animate-pulse' : 'bg-base-content/20'}`}
            />
            <span className="text-sm text-base-content/50">
              {connected ? 'Connected' : 'Disconnected'}
            </span>
          </div>
        </div>
      </div>

      {/* Emit Panel */}
      <div className="bg-base-200 rounded-xl border border-base-300 p-4">
        <div className="flex items-center gap-2 mb-3">
          <h3 className="text-sm font-semibold text-base-content">Emit Event</h3>
          <div className="flex gap-1 ml-auto">
            {presets
              .filter((p) => p.ns === namespace)
              .map((p) => (
                <button
                  key={p.label}
                  onClick={() => {
                    setEmitEvent(p.event);
                    setEmitPayload(p.payload);
                  }}
                  className="px-2 py-0.5 rounded text-[10px] bg-base-300 text-base-content/50 hover:text-base-content"
                >
                  {p.label}
                </button>
              ))}
          </div>
        </div>
        <div className="flex gap-2">
          <input
            type="text"
            value={emitEvent}
            onChange={(e) => setEmitEvent(e.target.value)}
            placeholder="event name"
            className="px-3 py-2 rounded-lg border border-base-300 bg-base-300 text-base-content text-sm w-48 font-mono"
          />
          <input
            type="text"
            value={emitPayload}
            onChange={(e) => setEmitPayload(e.target.value)}
            placeholder='{"key": "value"}'
            className="flex-1 px-3 py-2 rounded-lg border border-base-300 bg-base-300 text-base-content text-sm font-mono"
          />
          <button
            onClick={handleEmit}
            disabled={!connected || !emitEvent.trim()}
            className="px-4 py-2 rounded-lg bg-primary text-primary-content text-sm font-medium disabled:opacity-30"
          >
            Send
          </button>
        </div>
      </div>

      {/* Event Log */}
      <div className="bg-base-200 rounded-xl border border-base-300 overflow-hidden">
        <div className="flex items-center justify-between px-4 py-2 border-b border-base-300">
          <h3 className="text-sm font-semibold text-base-content">Event Log ({events.length})</h3>
          <button
            onClick={() => setEvents([])}
            className="text-xs text-base-content/40 hover:text-base-content"
          >
            Clear
          </button>
        </div>
        <div className="max-h-[400px] overflow-y-auto font-mono text-xs divide-y divide-base-300/30">
          {events.length === 0 ? (
            <div className="p-6 text-center text-base-content/30">
              No events yet. Connect to a namespace and events will appear here.
            </div>
          ) : (
            events.map((ev) => (
              <div
                key={ev.id}
                className={`px-4 py-2 ${ev.direction === 'in' ? 'bg-transparent' : 'bg-primary/5'}`}
              >
                <div className="flex items-center gap-2 mb-0.5">
                  <span className="text-base-content/30 w-20 shrink-0">{ev.time}</span>
                  <span
                    className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${
                      ev.direction === 'in' ? 'bg-success/15 text-success' : 'bg-info/15 text-info'
                    }`}
                  >
                    {ev.direction === 'in' ? 'IN' : 'OUT'}
                  </span>
                  <span
                    className={`font-medium ${
                      ev.event.includes('error')
                        ? 'text-error'
                        : ev.event.includes('connect')
                          ? 'text-success'
                          : 'text-base-content'
                    }`}
                  >
                    {ev.event}
                  </span>
                </div>
                <pre className="text-base-content/50 whitespace-pre-wrap break-all ml-[88px]">
                  {typeof ev.data === 'string' ? ev.data : JSON.stringify(ev.data, null, 2)}
                </pre>
              </div>
            ))
          )}
          <div ref={eventsEndRef} />
        </div>
      </div>
    </div>
  );
}

export default DevToolsPage;
